import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Grid,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout from '../../components/layout/ManagementPageLayout';
import FolderTabs from '../../components/common/FolderTabs';
import { ManagementContentSkeleton } from '../../components/layout/ManagementPageSkeleton';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import MembershipPlanCard from '../../components/membership-plans/MembershipPlanCard';
import MembershipRevenueSettingsCard from '../../components/membership-plans/MembershipRevenueSettingsCard';
import MembershipPlanEditorDialog from '../../components/membership-plans/MembershipPlanEditorDialog';
import CustomerMembershipsManagementPanel from '../../components/customer-memberships/CustomerMembershipsManagementPanel';
import { useToast } from '../../context/ToastContext';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import MembershipPlanService from '../../services/membershipPlanService';
import useStoreConsentForms from '../../hooks/useStoreConsentForms';
import employeePaymentService from '../../services/employeePaymentService';
import TeamMemberService from '../../services/teamMemberService';
import { filterAgendaVisibleTeamMembers } from '../../utils/teamMemberBookingUtils';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { getLocalizedValue } from '../../constants/Languages';
import useStripeConnectOnboarding from '../../hooks/useStripeConnectOnboarding';
import StoreService from '../../services/storeService';
import { getManagementMemberships } from '../../services/storeCustomerMembershipService';
import {
  CustomerPaymentMode,
  normalizeCustomerPaymentMode,
} from '../../constants/CustomerPaymentMode';

const CLOSED_DIALOG_STATE = { open: false, mode: 'create', plan: null };
const CUSTOMER_MEMBERSHIPS_TAB = 'customer-memberships';
const SETTINGS_TAB = 'settings';

const resolveActiveTab = (tabParam) => {
  if (tabParam === CUSTOMER_MEMBERSHIPS_TAB) {
    return CUSTOMER_MEMBERSHIPS_TAB;
  }

  if (tabParam === SETTINGS_TAB) {
    return SETTINGS_TAB;
  }

  return 'plans';
};

const MembershipPlansPage = () => {
  const { t, i18n } = useTranslation();
  const { storeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError, showSuccess } = useToast();
  const { hasPermission, loading: permissionsLoading } = usePermissions(storeId);
  const canWritePlans = hasPermission(Permissions.MembershipsWrite);
  const consentForms = useStoreConsentForms(storeId);
  const requestedTab = resolveActiveTab(searchParams.get('tab'));
  const activeTab = !canWritePlans && requestedTab === SETTINGS_TAB ? 'plans' : requestedTab;
  const { isMembershipBillingReady } = useStripeConnectOnboarding(storeId, {
    enabled: Boolean(storeId) && canWritePlans,
  });

  const [plans, setPlans] = useState([]);
  const [planContext, setPlanContext] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [storeTeamMembers, setStoreTeamMembers] = useState([]);
  const [revenueSettings, setRevenueSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [membershipsError, setMembershipsError] = useState(null);
  const [revenueSettingsError, setRevenueSettingsError] = useState(null);
  const [dialogState, setDialogState] = useState(CLOSED_DIALOG_STATE);
  const [statusChangePlan, setStatusChangePlan] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [statusChangePlanId, setStatusChangePlanId] = useState(null);
  const [paymentModeConfirmState, setPaymentModeConfirmState] = useState({ open: false, plan: null, action: null });
  const loadedForStoreRef = useRef(null);

  const loadPageData = useCallback(async () => {
    if (!storeId) {
      setError(t('membershipPlansManagement.error.noStoreSelected'));
      setLoading(false);
      return;
    }

    const requestKey = `membership-plans-page-${storeId}`;

    return requestDeduplicator.deduplicate(requestKey, async () => {
      try {
        setLoading(true);
        setError(null);
        setMembershipsError(null);
        setRevenueSettingsError(null);

        const [planData, membershipsResult, revenueSettingsResult, teamMembersResult] = await Promise.all([
          MembershipPlanService.getMembershipPlanManagementData(storeId),
          getManagementMemberships(storeId),
          canWritePlans
            ? employeePaymentService.getMembershipRevenueSettings(storeId)
            : Promise.resolve(null),
          TeamMemberService.getTeamMembersByStore(storeId).catch(() => []),
        ]);

        setPlanContext(planData);
        setPlans(planData.plans);
        setMemberships(membershipsResult);
        setRevenueSettings(revenueSettingsResult);
        setStoreTeamMembers(filterAgendaVisibleTeamMembers(teamMembersResult));
      } catch (loadError) {
        console.error('Failed to load membership plans page data:', loadError);
        setError(t('membershipPlansManagement.error.loading'));
      } finally {
        setLoading(false);
      }
    });
  }, [canWritePlans, storeId, t]);

  const membershipPaymentMode = normalizeCustomerPaymentMode(planContext?.membershipCustomerPaymentMode);
  const appointmentPaymentMode = normalizeCustomerPaymentMode(planContext?.appointmentCustomerPaymentMode);
  const requiresStripeForMembershipPlans = membershipPaymentMode === CustomerPaymentMode.OnlineOnly;

  const handleTabChange = (nextTab) => {
    if (nextTab === 'plans') {
      setSearchParams({}, { replace: true });
      return;
    }

    setSearchParams({ tab: nextTab }, { replace: true });
  };

  const refreshPlans = useCallback(async () => {
    if (!storeId) {
      return;
    }

    const data = await MembershipPlanService.getMembershipPlanManagementData(storeId);
    setPlanContext(data);
    setPlans(data.plans);
  }, [storeId]);

  useEffect(() => {
    if (!storeId || permissionsLoading) {
      return;
    }

    if (loadedForStoreRef.current === storeId) {
      return;
    }

    loadedForStoreRef.current = storeId;
    loadPageData();
  }, [loadPageData, permissionsLoading, storeId]);

  const persistPlan = async (plan) => {
    if (dialogState.mode === 'edit' && dialogState.plan?.id) {
      await MembershipPlanService.updatePlan(storeId, dialogState.plan.id, plan);
      showSuccess(t('membershipPlansManagement.feedback.updated'));
    } else {
      await MembershipPlanService.createPlan(storeId, plan);
      showSuccess(t('membershipPlansManagement.feedback.created'));
    }

    setDialogState(CLOSED_DIALOG_STATE);
    await refreshPlans();
  };

  const shouldConfirmPaymentModeChange = () => (
    requiresStripeForMembershipPlans && !isMembershipBillingReady
  );

  const handleSavePlan = async (plan) => {
    if (dialogState.mode === 'create' && shouldConfirmPaymentModeChange()) {
      setPaymentModeConfirmState({ open: true, plan, action: 'create' });
      return;
    }

    try {
      setSavingPlan(true);
      await persistPlan(plan);
    } catch (saveError) {
      console.error('Failed to save membership plan:', saveError);

      const errorCode = saveError?.response?.data?.code;
      if (errorCode === 'ConnectedAccountMembershipBillingNotReady') {
        showError(t('membershipPlansManagement.error.billingNotReady'));
      } else {
        showError(t(dialogState.mode === 'edit' ? 'membershipPlansManagement.error.update' : 'membershipPlansManagement.error.create'));
      }
    } finally {
      setSavingPlan(false);
    }
  };

  const handleConfirmPaymentModeChange = async () => {
    const pendingPlan = paymentModeConfirmState.plan;
    const pendingAction = paymentModeConfirmState.action;

    if (!storeId) {
      setPaymentModeConfirmState({ open: false, plan: null, action: null });
      return;
    }

    if (pendingAction === 'create' && !pendingPlan) {
      setPaymentModeConfirmState({ open: false, plan: null, action: null });
      return;
    }

    try {
      setSavingPlan(true);

      await StoreService.updatePaymentSettings(storeId, {
        appointmentCustomerPaymentMode: appointmentPaymentMode,
        membershipCustomerPaymentMode: CustomerPaymentMode.Both,
      });

      if (pendingAction === 'create') {
        await MembershipPlanService.createPlan(storeId, pendingPlan);
        showSuccess(t('membershipPlansManagement.feedback.created'));
        setDialogState(CLOSED_DIALOG_STATE);
      } else if (pendingAction === 'activate' && paymentModeConfirmState.planId) {
        await MembershipPlanService.activatePlan(storeId, paymentModeConfirmState.planId);
        showSuccess(t('membershipPlansManagement.feedback.activated'));
        setStatusChangePlan(null);
      }

      setPaymentModeConfirmState({ open: false, plan: null, action: null });
      await refreshPlans();
    } catch (saveError) {
      console.error('Failed to adjust payment settings for membership plan:', saveError);
      showError(t('membershipPlansManagement.paymentModeAdjust.error'));
    } finally {
      setSavingPlan(false);
    }
  };

  const handleTogglePlanStatus = async () => {
    const targetPlan = statusChangePlan;
    if (!targetPlan) {
      return;
    }

    const isActive = MembershipPlanService.isMembershipPlanActive(targetPlan.status);

    if (!isActive && shouldConfirmPaymentModeChange()) {
      setStatusChangePlan(null);
      setPaymentModeConfirmState({
        open: true,
        plan: null,
        planId: targetPlan.id,
        action: 'activate',
      });
      return;
    }

    try {
      setStatusChangePlanId(targetPlan.id);

      if (isActive) {
        await MembershipPlanService.deactivatePlan(storeId, targetPlan.id);
        showSuccess(t('membershipPlansManagement.feedback.deactivated'));
      } else {
        await MembershipPlanService.activatePlan(storeId, targetPlan.id);
        showSuccess(t('membershipPlansManagement.feedback.activated'));
      }

      await refreshPlans();
    } catch (statusError) {
      console.error('Failed to update membership plan status:', statusError);

      const errorCode = statusError?.response?.data?.code;
      if (errorCode === 'ConnectedAccountMembershipBillingNotReady') {
        showError(t('membershipPlansManagement.error.billingNotReady'));
      } else {
        showError(t(isActive
          ? 'membershipPlansManagement.error.deactivate'
          : 'membershipPlansManagement.error.activate'));
      }
    } finally {
      setStatusChangePlanId(null);
      setStatusChangePlan(null);
    }
  };

  const handleRevenueSettingsSaved = (settings) => {
    setRevenueSettings(settings);
  };

  const pageSubtitle = t('membershipPlansManagement.subtitle');

  const renderPlansContent = () => {
    if (loading) {
      return <ManagementContentSkeleton variant="cards" withAction={canWritePlans} />;
    }

    if (error) {
      return (
        <Box sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={loadPageData}>
            {t('common.retry')}
          </Button>
        </Box>
      );
    }

    return (
      <Box id="membership-plans-content" sx={{ p: 4 }}>
        {canWritePlans ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4, pt: 1 }}>
            <PrimaryActionButton
              startIcon={<AddIcon />}
              onClick={() => setDialogState({ open: true, mode: 'create', plan: null })}
              disabled={!canWritePlans}
              sx={{
                px: 4,
                py: 1.5,
              }}
            >
              {t('membershipPlansManagement.addNew')}
            </PrimaryActionButton>
          </Box>
        ) : null}

        {plans.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('membershipPlansManagement.noPlans')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('membershipPlansManagement.emptyHint')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3} p={1} sx={{ pt: 2 }}>
            {plans.map((plan) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={plan.id}>
                <MembershipPlanCard
                  plan={plan}
                  currency={planContext?.currency}
                  loading={savingPlan || statusChangePlanId === plan.id}
                  canEdit={canWritePlans}
                  onEdit={() => setDialogState({ open: true, mode: 'edit', plan })}
                  onToggleStatus={() => setStatusChangePlan(plan)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  const membershipTabs = [
    { value: 'plans', label: t('membershipPlansManagement.tabs.plans') },
    {
      value: CUSTOMER_MEMBERSHIPS_TAB,
      label: t('membershipPlansManagement.tabs.customerMemberships'),
      mobileLabel: t('membershipPlansManagement.tabs.customerMembershipsShort'),
    },
    ...(canWritePlans ? [{ value: SETTINGS_TAB, label: t('membershipPlansManagement.tabs.settings') }] : []),
  ];

  return (
    <ManagementPageLayout
      id="membership-plans-page"
      containerId="membership-plans-header"
      titleId="membership-plans-title"
      subtitleId="membership-plans-subtitle"
      pageTitle={t('membershipPlansManagement.title')}
      pageSubtitle={pageSubtitle}
      contentId="membership-plans-main-content"
    >
      <FolderTabs
        tabs={membershipTabs}
        value={activeTab}
        onChange={handleTabChange}
        contentId="membership-plans-card"
        equalWidthOnMobile
      >
        <Box role="tabpanel" hidden={activeTab !== 'plans'}>
          {renderPlansContent()}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== CUSTOMER_MEMBERSHIPS_TAB}>
          <CustomerMembershipsManagementPanel
            embedded
            planContext={planContext}
            planContextLoading={loading}
            memberships={memberships}
            onMembershipsChange={setMemberships}
            membershipsLoading={loading}
            membershipsError={membershipsError}
          />
        </Box>

        {canWritePlans ? (
          <Box role="tabpanel" hidden={activeTab !== SETTINGS_TAB}>
            <Box sx={{ p: 4 }}>
              <MembershipRevenueSettingsCard
                storeId={storeId}
                canEdit={canWritePlans}
                asTabPanel
                initialSettings={revenueSettings}
                settingsLoading={loading}
                settingsError={revenueSettingsError}
                currency={planContext?.currency || 'EUR'}
                onSettingsSaved={handleRevenueSettingsSaved}
              />
            </Box>
          </Box>
        ) : null}
      </FolderTabs>

      <MembershipPlanEditorDialog
        open={dialogState.open}
        mode={dialogState.mode}
        plan={dialogState.plan}
        supportedLanguages={planContext?.supportedLanguages || ['en']}
        currency={planContext?.currency || 'EUR'}
        services={planContext?.services || []}
        teamMembers={storeTeamMembers}
        consentForms={consentForms}
        saving={savingPlan}
        onClose={() => setDialogState(CLOSED_DIALOG_STATE)}
        onSave={handleSavePlan}
      />

      <ConfirmDialog
        open={paymentModeConfirmState.open}
        onClose={() => setPaymentModeConfirmState({ open: false, plan: null, action: null })}
        onConfirm={handleConfirmPaymentModeChange}
        title={t('membershipPlansManagement.paymentModeAdjust.title')}
        message={t('membershipPlansManagement.paymentModeAdjust.message')}
        confirmText={t('membershipPlansManagement.paymentModeAdjust.confirm')}
        cancelText={t('common.cancel')}
      />

      <ConfirmDialog
        open={Boolean(statusChangePlan)}
        onClose={() => setStatusChangePlan(null)}
        onConfirm={handleTogglePlanStatus}
        title={t(MembershipPlanService.isMembershipPlanActive(statusChangePlan?.status)
          ? 'membershipPlansManagement.deactivate.title'
          : 'membershipPlansManagement.activate.title')}
        message={t(MembershipPlanService.isMembershipPlanActive(statusChangePlan?.status)
          ? 'membershipPlansManagement.deactivate.message'
          : 'membershipPlansManagement.activate.message', {
          name: statusChangePlan
            ? getLocalizedValue(statusChangePlan.name, i18n.language, undefined, '')
            : '',
        })}
        confirmText={t(MembershipPlanService.isMembershipPlanActive(statusChangePlan?.status)
          ? 'membershipPlansManagement.deactivate.confirm'
          : 'membershipPlansManagement.activate.confirm')}
        cancelText={t(MembershipPlanService.isMembershipPlanActive(statusChangePlan?.status)
          ? 'membershipPlansManagement.deactivate.cancel'
          : 'membershipPlansManagement.activate.cancel')}
      />
    </ManagementPageLayout>
  );
};

export default MembershipPlansPage;
